import type { Fetcher, TokenPair } from './fetcher';
import type { components } from './schema';
import {
  localeOutSchema,
  meOutSchema,
  sessionOutSchema,
  tenantOutSchema,
  tokenOutSchema,
  totpEnrolOutSchema,
  userOutSchema,
  userPageSchema,
} from './schemas';

/**
 * One function per BP-01 §5 route the web apps use, typed end to end from the
 * generated OpenAPI types. Nothing here branches: the transport decisions live
 * in `./fetcher`, the error decisions in `./problem`.
 *
 * §5 routes deliberately **not** wrapped, because no §6 screen reads them:
 * `/outlets`, `/events`, `/settings/{key}`, `/secrets/{key}`, `/readyz`.
 */

type Schemas = components['schemas'];

export type LoginIn = Schemas['LoginIn'];
export type MeOut = Schemas['MeOut'];
export type SessionOut = Schemas['SessionOut'];
export type TenantIn = Schemas['TenantIn'];
export type TenantOut = Schemas['TenantOut'];
export type TenantBrief = Schemas['TenantBrief'];
export type UserBrief = Schemas['UserBrief'];
export type UserIn = Schemas['UserIn'];
export type UserOut = Schemas['UserOut'];
export type UserPage = Schemas['UserPage'];
export type UserPatch = Schemas['UserPatch'];
export type RolesIn = Schemas['RolesIn'];
export type Role = Schemas['Role'];
export type TotpEnrolOut = Schemas['TotpEnrolOut'];
export type TokenIn = Schemas['TokenIn'];
export type Locale = Schemas['Locale'];
export type LocaleOut = Schemas['LocaleOut'];

/** Every api call the §6 screens make. */
export interface Api {
  login: (body: LoginIn) => Promise<SessionOut>;
  logout: () => Promise<void>;
  me: () => Promise<MeOut>;
  /** BP-19 §5: this caller's own language, user row and client row alike. */
  setLocale: (locale: Locale) => Promise<LocaleOut>;
  readTenant: () => Promise<TenantOut>;
  updateTenant: (body: TenantIn) => Promise<TenantOut>;
  listUsers: () => Promise<UserPage>;
  createUser: (body: UserIn, idempotencyKey: string) => Promise<UserOut>;
  patchUser: (id: string, body: UserPatch) => Promise<UserOut>;
  setRoles: (id: string, body: RolesIn) => Promise<UserOut>;
  totpEnrol: () => Promise<TotpEnrolOut>;
  totpVerify: (code: string) => Promise<void>;
  /** R6's rotation, used by the bearer refresh interceptor. */
  refresh: (refresh: string) => Promise<TokenPair>;
}

/** Bind the §5 routes to a transport. */
export function createApi(fetcher: Fetcher): Api {
  return {
    login: (body) => fetcher.json('/v1/auth/login', { method: 'POST', body }, sessionOutSchema),
    logout: () => fetcher.empty('/v1/auth/logout', { method: 'POST' }),
    me: () => fetcher.json('/v1/me', { method: 'GET' }, meOutSchema),
    setLocale: (locale) =>
      fetcher.json('/v1/me/locale', { method: 'PUT', body: { locale } }, localeOutSchema),
    readTenant: () => fetcher.json('/v1/tenant', { method: 'GET' }, tenantOutSchema),
    updateTenant: (body) => fetcher.json('/v1/tenant', { method: 'PUT', body }, tenantOutSchema),
    listUsers: () => fetcher.json('/v1/users', { method: 'GET' }, userPageSchema),
    createUser: (body, idempotencyKey) =>
      fetcher.json(
        '/v1/users',
        { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } },
        userOutSchema,
      ),
    patchUser: (id, body) =>
      fetcher.json(`/v1/users/${id}`, { method: 'PATCH', body }, userOutSchema),
    setRoles: (id, body) =>
      fetcher.json(`/v1/users/${id}/roles`, { method: 'PUT', body }, userOutSchema),
    totpEnrol: () => fetcher.json('/v1/auth/totp/enrol', { method: 'POST' }, totpEnrolOutSchema),
    totpVerify: (code) => fetcher.empty('/v1/auth/totp/verify', { method: 'POST', body: { code } }),
    refresh: (refresh) =>
      fetcher.json('/v1/auth/refresh', { method: 'POST', body: { refresh } }, tokenOutSchema),
  };
}
